// ── Coastlines and borders over the imagery ──
// Satellite, radar, ocean and futurecast pictures sit on top of the basemap
// and hide its coastlines and borders, so it's hard to tell at a glance where
// the weather is. While any of them is on, these lines are drawn above all
// the imagery (and under the place labels): coastlines and country borders
// bright with a dark casing, so they read on white cloud tops and on dark
// ocean alike, state borders lighter. Drawn from the basemap's own vector
// tiles (CARTO dark-matter: source "carto", the water polygons' edges are the
// coastlines). With no imagery on they're hidden and the map is as it was.

import type maplibregl from "maplibre-gl";

/** The lowest of these layers: imagery is inserted below it (frameEngine). */
export const REF_BELOW = "ref-coast-casing";
const IDS = [REF_BELOW, "ref-country-casing", "ref-coast", "ref-country", "ref-state"];

const zoomed = (z2: number, z5: number, z8: number) =>
  ["interpolate", ["linear"], ["zoom"], 2, z2, 5, z5, 8, z8] as unknown as number;
const COUNTRY = ["all", ["==", "admin_level", 2], ["==", "maritime", 0]] as maplibregl.FilterSpecification;
const STATE = ["all", ["==", "admin_level", 4], ["==", "maritime", 0]] as maplibregl.FilterSpecification;
const WATER = ["==", "$type", "Polygon"] as maplibregl.FilterSpecification;

/** Add the lines (hidden) under the first label layer. Idempotent. */
export function addReferenceLines(map: maplibregl.Map) {
  if (!map.getSource("carto") || map.getLayer(REF_BELOW)) return;
  const below = map.getStyle()?.layers?.find((l) => l.type === "symbol" && !l.id.startsWith("graticule"))?.id;
  const add = (layer: maplibregl.LineLayerSpecification) =>
    map.addLayer({ ...layer, layout: { visibility: "none", "line-join": "round", "line-cap": "round" } }, below);
  add({
    id: REF_BELOW, type: "line", source: "carto", "source-layer": "water", filter: WATER,
    paint: { "line-color": "rgba(0, 0, 0, 0.55)", "line-width": zoomed(2.2, 3, 3.6) },
  });
  add({
    id: "ref-country-casing", type: "line", source: "carto", "source-layer": "boundary", filter: COUNTRY,
    paint: { "line-color": "rgba(0, 0, 0, 0.45)", "line-width": zoomed(2, 2.6, 3.2) },
  });
  add({
    id: "ref-coast", type: "line", source: "carto", "source-layer": "water", filter: WATER,
    paint: { "line-color": "rgba(236, 244, 252, 0.9)", "line-width": zoomed(0.9, 1.25, 1.6) },
  });
  add({
    id: "ref-country", type: "line", source: "carto", "source-layer": "boundary", filter: COUNTRY,
    paint: { "line-color": "rgba(236, 244, 252, 0.8)", "line-width": zoomed(0.8, 1.1, 1.4) },
  });
  add({
    id: "ref-state", type: "line", source: "carto", "source-layer": "boundary", filter: STATE, minzoom: 3,
    paint: { "line-color": "rgba(236, 244, 252, 0.45)", "line-width": zoomed(0.5, 0.7, 1) },
  });
}

export function showReferenceLines(map: maplibregl.Map, on: boolean) {
  for (const id of IDS) if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", on ? "visible" : "none");
}
